/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { userSessionIssuersCimdClientsCreate } from "../funcs/userSessionIssuersCimdClientsCreate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { CreateUserSessionIssuerCimdClientResult } from "../models/components/createusersessionissuercimdclientresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CreateUserSessionIssuerCimdClientRequest,
  CreateUserSessionIssuerCimdClientSecurity,
} from "../models/operations/createusersessionissuercimdclient.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CreateUserSessionIssuerCimdClientMutationVariables = {
  request: CreateUserSessionIssuerCimdClientRequest;
  security?: CreateUserSessionIssuerCimdClientSecurity | undefined;
  options?: RequestOptions;
};

export type CreateUserSessionIssuerCimdClientMutationData =
  CreateUserSessionIssuerCimdClientResult;

export type CreateUserSessionIssuerCimdClientMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * createUserSessionIssuerCimdClient userSessionIssuersCimdClients
 *
 * @remarks
 * Allow an additional CIMD document URL on a user_session_issuer, beyond the preset catalog. The URL is validated for draft-ietf-oauth-client-id-metadata-document-02 §3 syntax and rejected outright when malformed. The document itself is deliberately NOT fetched here: a vendor's host being briefly unreachable must not block configuration, and an advisory warning nobody can act on is not worth an outbound request on every write. Call verifyURL first to check that the document is reachable and valid.
 */
export function useCreateUserSessionIssuerCimdClientMutation(
  options?: MutationHookOptions<
    CreateUserSessionIssuerCimdClientMutationData,
    CreateUserSessionIssuerCimdClientMutationError,
    CreateUserSessionIssuerCimdClientMutationVariables
  >,
): UseMutationResult<
  CreateUserSessionIssuerCimdClientMutationData,
  CreateUserSessionIssuerCimdClientMutationError,
  CreateUserSessionIssuerCimdClientMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCreateUserSessionIssuerCimdClientMutation(client, options),
    ...options,
  });
}

export function mutationKeyCreateUserSessionIssuerCimdClient(): MutationKey {
  return ["@gram/client", "userSessionIssuersCimdClients", "create"];
}

export function buildCreateUserSessionIssuerCimdClientMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CreateUserSessionIssuerCimdClientMutationVariables,
  ) => Promise<CreateUserSessionIssuerCimdClientMutationData>;
} {
  return {
    mutationKey: mutationKeyCreateUserSessionIssuerCimdClient(),
    mutationFn: function createUserSessionIssuerCimdClientMutationFn({
      request,
      security,
      options,
    }): Promise<CreateUserSessionIssuerCimdClientMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(userSessionIssuersCimdClientsCreate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
